// =============================================
// MarketOverview.jsx - 시장 현황판
// =============================================
// 탭 구성:
//   [가상화폐] [코스피] [코스닥] [나스닥] [다우 산업]
//   × [시가총액 TOP10] [상승률 TOP10] [하락률 TOP10]
//
// ★ 차트 엔진 이원화:
//   - 가상화폐 탭: TradingViewWidget (BINANCE:BTCUSDT 형식)
//   - 주식·지수 탭: YahooChart (yfinance 티커, lightweight-charts 렌더링)
//     한국 주식: "005930.KS" (백엔드 ticker 그대로 사용)
//     미국 주식: "AAPL", "UNH" (거래소 접두어 없이)
//
// ★ 클릭 → 차트 흐름:
//   ① 행 클릭 → handleRowClick 실행
//   ② marketTab에 따라 chartEngine + 티커/심볼 결정
//   ③ selectedRow 상태 업데이트 → React 리렌더링
//   ④ 차트 패널이 테이블 아래에 나타남
//   ⑤ scrollIntoView로 부드럽게 이동
// =============================================

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import TradingViewWidget from './TradingViewWidget';
import YahooChart from './YahooChart';

const API_URL = 'http://localhost:8000';

// TradingView (코인) 봉 단위
const TV_INTERVALS = [
  { label: '15분', value: '15'  },
  { label: '1시간', value: '60'  },
  { label: '4시간', value: '240' },
  { label: '일봉',  value: 'D'   },
  { label: '주봉',  value: 'W'   },
];

// Yahoo Finance (주식·지수) 봉 단위 — yfinance가 지원하는 형식
const YAHOO_INTERVALS = [
  { label: '15분', value: '15m' },
  { label: '1시간', value: '1h'  },
  { label: '일봉',  value: '1d'  },
  { label: '주봉',  value: '1w'  },
];

// 하위 호환 alias
const CHART_INTERVALS = TV_INTERVALS;

// ── 시장 탭: 가상화폐 / 한국 / 미국 ──
const MARKET_TABS = [
  { id: 'crypto', label: '가상화폐' },
  { id: 'kospi',  label: '코스피'   },
  { id: 'kosdaq', label: '코스닥'   },
  { id: 'nasdaq', label: '나스닥'   },
  { id: 'dow',    label: '다우 산업' },
];

// ── 서브탭: 모든 시장에서 동일하게 제공 ──
const SUB_TABS = [
  { id: 'top10',   label: '시가총액 순'  },
  { id: 'gainers', label: '상승률 TOP10' },
  { id: 'losers',  label: '하락률 TOP10' },
];


// =============================================
// ★★★ getTradingViewSymbol — 심볼 변환 핵심 함수 ★★★
// =============================================
// TradingView 차트는 반드시 "거래소:심볼코드" 형태가 필요해.
// 이 형태가 틀리면 TradingView가 기본값(AAPL)을 보여줘!
//
// ┌──────────────────────────────────────────────────────────────┐
// │  type = 'kr_stock'  → "005930.KS"     → "KRX:005930"       │
// │         ① split('.') → ["005930","KS"] ② KRX: 붙이기        │
// │                                                              │
// │  type = 'kr_index'  → "KOSPI"         → "TVC:KOSPI"        │
// │         TVC = TradingView 자체 데이터 (가장 안정적)           │
// │                                                              │
// │  type = 'us_stock'  → {ticker:"AAPL", exchange:"NASDAQ"}    │
// │                     → "NASDAQ:AAPL"   ← 거래소:종목코드      │
// │         NYSE 종목이면 → "NYSE:UNH"                           │
// │                                                              │
// │  type = 'crypto'    → "BTC"           → "BINANCE:BTCUSDT"   │
// └──────────────────────────────────────────────────────────────┘
function getTradingViewSymbol(type, identifier) {
  switch (type) {

    case 'kr_index': {
      // 한국 지수: TVC 사용 (KRX:KOSPI보다 안정적)
      const MAP = { KOSPI: 'TVC:KOSPI', KOSDAQ: 'TVC:KOSDAQ' };
      return MAP[identifier] || `TVC:${identifier}`;
    }

    case 'kr_stock': {
      // 한국 주식: ".KS" ".KQ" 제거 후 "KRX:" 접두어
      // "005930.KS" → split('.')[0] → "005930" → "KRX:005930"
      const code = String(identifier).split('.')[0];
      return `KRX:${code}`;
    }

    case 'us_stock': {
      // 미국 주식: 백엔드가 exchange 필드를 함께 줘.
      // identifier = { ticker: "AAPL", exchange: "NASDAQ" }
      // → "NASDAQ:" + "AAPL" → "NASDAQ:AAPL"
      // identifier = { ticker: "UNH", exchange: "NYSE" }
      // → "NYSE:UNH"
      const { ticker, exchange } = identifier;
      return `${exchange}:${ticker}`;
    }

    case 'global_index': {
      // 글로벌 지수
      const MAP = { DJI: 'TVC:DJI', IXIC: 'NASDAQ:IXIC', SP500: 'SP:SPX' };
      return MAP[identifier] || `TVC:${identifier}`;
    }

    case 'commodity': {
      // 상품 선물: GC1! = 금 연속 선물, SI1! = 은 연속 선물
      const MAP = { GOLD: 'COMEX:GC1!', SILVER: 'COMEX:SI1!' };
      return MAP[identifier] || `COMEX:${identifier}`;
    }

    case 'crypto': {
      // 암호화폐: Binance USDT 페어
      // "BTC" → "BINANCE:BTCUSDT"
      return `BINANCE:${String(identifier).toUpperCase()}USDT`;
    }

    default:
      return String(identifier);
  }
}


export default function MarketOverview() {
  const [marketTab, setMarketTab] = useState('crypto');
  const [subTab,    setSubTab]    = useState('top10');

  // 각 시장 데이터 (처음엔 null, 로딩 완료 후 채워져)
  const [cryptoTop10,   setCryptoTop10]   = useState(null);
  const [cryptoGainers, setCryptoGainers] = useState(null);
  const [cryptoLosers,  setCryptoLosers]  = useState(null);
  const [kospiData,     setKospiData]     = useState(null);  // { stocks, gainers, losers }
  const [kosdaqData,    setKosdaqData]    = useState(null);
  const [nasdaqData,    setNasdaqData]    = useState(null);  // { stocks, gainers, losers }
  const [dowData,       setDowData]       = useState(null);
  const [loading,       setLoading]       = useState(false);

  // 클릭된 종목 차트 상태
  // selectedRow: null이면 차트 닫힘
  // 형태: { chartEngine, tvSymbol?, yahooTicker?, label }
  //   chartEngine='tradingview' → tvSymbol 사용
  //   chartEngine='yahoo'       → yahooTicker 사용
  const [selectedRow,   setSelectedRow]   = useState(null);
  const [tvInterval,    setTvInterval]    = useState('D');    // 코인용 봉 단위
  const [yahooInterval, setYahooInterval] = useState('1d');   // 주식·지수용 봉 단위
  // 하위 호환
  const chartInterval    = tvInterval;
  const setChartInterval = setTvInterval;
  const chartRef = useRef(null);


  // =============================================
  // 탭 전환 시 해당 데이터 fetch
  // =============================================
  // 이미 로드한 데이터는 다시 요청하지 않아 (캐시 역할)
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      try {
        if (marketTab === 'crypto') {
          const p = [];
          if (!cryptoTop10)   p.push(axios.get(`${API_URL}/api/market/crypto/top10`).then(r => setCryptoTop10(r.data.coins)));
          if (!cryptoGainers) p.push(axios.get(`${API_URL}/api/market/crypto/gainers`).then(r => setCryptoGainers(r.data.coins)));
          if (!cryptoLosers)  p.push(axios.get(`${API_URL}/api/market/crypto/losers`).then(r => setCryptoLosers(r.data.coins)));
          await Promise.all(p);

        } else if (marketTab === 'kospi' && !kospiData) {
          const r = await axios.get(`${API_URL}/api/market/kr-stocks/kospi`);
          setKospiData(r.data);   // { stocks, gainers, losers }

        } else if (marketTab === 'kosdaq' && !kosdaqData) {
          const r = await axios.get(`${API_URL}/api/market/kr-stocks/kosdaq`);
          setKosdaqData(r.data);

        } else if (marketTab === 'nasdaq' && !nasdaqData) {
          // 나스닥 종목: exchange 필드 포함 → "NASDAQ:AAPL" 형태 변환 가능
          const r = await axios.get(`${API_URL}/api/market/us-stocks/nasdaq`);
          setNasdaqData(r.data);  // { stocks, gainers, losers }

        } else if (marketTab === 'dow' && !dowData) {
          const r = await axios.get(`${API_URL}/api/market/us-stocks/dow`);
          setDowData(r.data);
        }
      } catch (e) {
        console.error('시장 데이터 오류:', e);
      } finally {
        setLoading(false);
      }
    };
    go();
  }, [marketTab]); // eslint-disable-line


  // =============================================
  // 현재 탭 + 서브탭에 맞는 데이터 선택
  // =============================================
  const getRows = () => {
    if (marketTab === 'crypto') {
      if (subTab === 'top10')   return cryptoTop10;
      if (subTab === 'gainers') return cryptoGainers;
      if (subTab === 'losers')  return cryptoLosers;
    }
    if (marketTab === 'kospi') {
      if (subTab === 'top10')   return kospiData?.stocks;
      if (subTab === 'gainers') return kospiData?.gainers;
      if (subTab === 'losers')  return kospiData?.losers;
    }
    if (marketTab === 'kosdaq') {
      if (subTab === 'top10')   return kosdaqData?.stocks;
      if (subTab === 'gainers') return kosdaqData?.gainers;
      if (subTab === 'losers')  return kosdaqData?.losers;
    }
    if (marketTab === 'nasdaq') {
      if (subTab === 'top10')   return nasdaqData?.stocks;
      if (subTab === 'gainers') return nasdaqData?.gainers;
      if (subTab === 'losers')  return nasdaqData?.losers;
    }
    if (marketTab === 'dow') {
      if (subTab === 'top10')   return dowData?.stocks;
      if (subTab === 'gainers') return dowData?.gainers;
      if (subTab === 'losers')  return dowData?.losers;
    }
    return null;
  };

  const rows = getRows();


  // =============================================
  // ★ 종목 클릭 핸들러
  // =============================================
  // ① 행 클릭 → handleRowClick({ chartEngine, tvSymbol?, yahooTicker?, label })
  // ② selectedRow 상태 업데이트
  // ③ 차트 패널 렌더링 → ④ 스크롤
  const handleRowClick = (rowInfo) => {
    // 같은 종목 다시 클릭 → 닫기 (tvSymbol 또는 yahooTicker로 구분)
    const curKey = selectedRow?.tvSymbol || selectedRow?.yahooTicker;
    const newKey = rowInfo.tvSymbol      || rowInfo.yahooTicker;
    if (curKey && curKey === newKey) {
      setSelectedRow(null);
    } else {
      setSelectedRow(rowInfo);
      setTimeout(() => chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const handleMarketTabChange = (tabId) => {
    setMarketTab(tabId);
    setSelectedRow(null); // 탭 이동 시 차트 초기화
  };


  return (
    <div style={containerStyle}>
      <h3 style={{ margin: '0 0 1rem', color: '#e2e8f0' }}>
        시장 현황판
        <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#718096', fontWeight: 400 }}>
          종목 클릭 → 차트 보기
        </span>
      </h3>

      {/* ── 탭 영역 ── */}
      <div style={tabGroupStyle}>
        {/* 시장 탭 (가상화폐 / 코스피 / 코스닥 / 나스닥 / 다우 산업) */}
        {MARKET_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => handleMarketTabChange(t.id)}
            style={tabBtnStyle(marketTab === t.id, '#3182ce')}
          >
            {t.label}
          </button>
        ))}

        <div style={{ flex: 1 }} />

        {/* 서브탭 (시가총액 순 / 상승률 / 하락률) */}
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            style={tabBtnStyle(
              subTab === t.id,
              t.id === 'losers' ? '#c53030' : '#553c9a',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── 테이블 ── */}
      <div style={{ overflowX: 'auto' }}>
        {loading && <div style={loadingStyle}>데이터 불러오는 중...</div>}

        {!loading && rows && rows.length > 0 && (
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>종목</Th>
                <Th align="right">현재가</Th>
                <Th align="right">변동률</Th>
                {marketTab === 'crypto' && <Th align="right">시가총액</Th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                // =============================================
                // ② TradingView 심볼 계산 — 타입별 분기
                // =============================================
                // 각 시장 타입마다 다른 변환 규칙:
                //
                //   crypto  : row.symbol = "BTC"
                //             → getTradingViewSymbol('crypto', 'BTC')
                //             → "BINANCE:BTCUSDT"
                //
                //   kospi   : row.ticker = "005930.KS"
                //             → getTradingViewSymbol('kr_stock', '005930.KS')
                //             → "KRX:005930"  (.KS 제거 + KRX: 붙이기)
                //
                //   nasdaq  : row.ticker = "AAPL", row.exchange = "NASDAQ"
                //             → getTradingViewSymbol('us_stock', {ticker:'AAPL', exchange:'NASDAQ'})
                //             → "NASDAQ:AAPL"
                //
                //   dow     : row.ticker = "UNH", row.exchange = "NYSE"
                //             → "NYSE:UNH"
                // =============================================
                // ② 차트 엔진 결정 — marketTab에 따라 분기
                // =============================================
                // 코인: TradingViewWidget (BINANCE:BTCUSDT)
                // 주식·지수: YahooChart (yfinance 티커)
                //   한국 주식 ticker = "005930.KS" → Yahoo Finance 티커로 그대로 사용
                //   미국 주식 ticker = "AAPL"      → Yahoo Finance 티커로 그대로 사용
                let chartEngine, tvSymbol, yahooTicker, label;

                if (marketTab === 'crypto') {
                  // 가상화폐: TradingView 엔진
                  chartEngine = 'tradingview';
                  tvSymbol    = getTradingViewSymbol('crypto', row.symbol);
                  label       = row.symbol; // "BTC"
                } else if (marketTab === 'nasdaq' || marketTab === 'dow') {
                  // 미국 주식: Yahoo Finance 엔진 (ticker = "AAPL", "UNH" 등)
                  chartEngine  = 'yahoo';
                  yahooTicker  = row.ticker;  // yfinance는 거래소 접두어 없이 조회
                  label        = row.name;    // "애플", "유나이티드헬스" 등
                } else {
                  // 한국 주식 (kospi / kosdaq): Yahoo Finance 엔진
                  // ticker = "005930.KS" — Yahoo Finance와 yfinance가 모두 인식하는 형식!
                  chartEngine  = 'yahoo';
                  yahooTicker  = row.ticker;  // 예: "005930.KS", "035420.KQ"
                  label        = row.name;    // "삼성전자"
                }

                // 선택 여부 판단 (tvSymbol 또는 yahooTicker로)
                const rowKey     = tvSymbol || yahooTicker;
                const selKey     = selectedRow?.tvSymbol || selectedRow?.yahooTicker;
                const isSelected = !!selKey && selKey === rowKey;

                return (
                  <MarketRow
                    key={`${marketTab}-${row.ticker || row.symbol}-${i}`}
                    row={row}
                    rank={i + 1}
                    market={marketTab}
                    isSelected={isSelected}
                    // ① 행 클릭 → handleRowClick에 엔진 정보 포함해서 전달
                    onRowClick={() => handleRowClick({ chartEngine, tvSymbol, yahooTicker, label })}
                  />
                );
              })}
            </tbody>
          </table>
        )}

        {!loading && (!rows || rows.length === 0) && (
          <div style={loadingStyle}>
            {marketTab === 'nasdaq' || marketTab === 'dow'
              ? '미국 주식 데이터 로딩 중... (첫 로딩은 30~60초 걸릴 수 있어)'
              : '데이터를 가져오지 못했어. 백엔드 서버를 확인해봐.'}
          </div>
        )}
      </div>

      {/* =============================================
          ③ selectedRow가 있으면 차트 패널 표시
          ============================================= */}
      {selectedRow && (() => {
        const isYahoo     = selectedRow.chartEngine === 'yahoo';
        const intervals   = isYahoo ? YAHOO_INTERVALS : TV_INTERVALS;
        const curInterval = isYahoo ? yahooInterval   : tvInterval;
        const setInterval = isYahoo ? setYahooInterval : setTvInterval;
        const badgeTicker = isYahoo ? selectedRow.yahooTicker : selectedRow.tvSymbol;

        return (
        <div
          ref={chartRef}   // ④ scrollIntoView 목표
          style={{
            marginTop: '1.5rem',
            backgroundColor: '#161e2a',
            borderRadius: '14px',
            border: `1px solid ${isYahoo ? '#38a169' : '#3182ce'}`,
            overflow: 'hidden',
          }}
        >
          {/* 차트 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.25rem',
            flexWrap: 'wrap',
            gap: '0.5rem',
            borderBottom: '1px solid #2d3748',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>

              {/* ★ 현재 보고 있는 종목 크게 표시 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{
                  fontSize: '0.68rem', color: '#718096',
                  fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>
                  현재 보고 있는 종목
                </span>
                <span style={{ fontWeight: 'bold', color: '#e2e8f0', fontSize: '1.1rem' }}>
                  {selectedRow.label}
                </span>
              </div>

              {/* 티커 배지 — Yahoo는 yfinance 티커, TV는 TradingView 심볼 */}
              <span style={{
                fontSize: '0.72rem',
                color:           isYahoo ? '#68d391' : '#63b3ed',
                backgroundColor: isYahoo ? '#1c3829' : '#1e2d3d',
                padding: '3px 9px',
                borderRadius: '4px', fontFamily: 'monospace',
                border: `1px solid ${isYahoo ? '#276749' : '#2c5282'}`,
              }}>
                {badgeTicker}
              </span>

              {/* 차트 엔진 표시 */}
              <span style={{
                fontSize: '0.65rem',
                color:           isYahoo ? '#48bb78' : '#63b3ed',
                backgroundColor: '#2d3748',
                padding: '2px 7px', borderRadius: '4px',
              }}>
                {isYahoo ? '📊 Yahoo Finance' : '📈 TradingView'}
              </span>

              {/* 봉 단위 버튼 — 엔진에 맞는 목록 */}
              <div style={{ display: 'flex', gap: '0.3rem' }}>
                {intervals.map(iv => (
                  <button
                    key={iv.value}
                    onClick={() => setInterval(iv.value)}
                    style={{
                      padding: '0.25rem 0.65rem', border: 'none',
                      borderRadius: '5px', cursor: 'pointer',
                      fontSize: '0.75rem', fontWeight: 'bold',
                      backgroundColor: curInterval === iv.value ? '#553c9a' : '#2d3748',
                      color: curInterval === iv.value ? '#e9d8fd' : '#718096',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    {iv.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setSelectedRow(null)}
              style={{
                background: 'none', border: '1px solid #4a5568',
                color: '#718096', borderRadius: '6px', cursor: 'pointer',
                padding: '0.25rem 0.9rem', fontSize: '0.8rem',
              }}
            >
              ✕ 닫기
            </button>
          </div>

          {/* ★ 차트 렌더링 — chartEngine에 따라 분기 */}
          {selectedRow.chartEngine === 'yahoo' ? (
            // Yahoo Finance 차트 (주식·지수)
            <YahooChart
              key={`${selectedRow.yahooTicker}-${yahooInterval}`}
              ticker={selectedRow.yahooTicker}
              interval={yahooInterval}
              height={800}
            />
          ) : (
            // TradingView 차트 (코인)
            <TradingViewWidget
              key={selectedRow.tvSymbol}
              symbol={selectedRow.tvSymbol}
              interval={tvInterval}
              height={800}
            />
          )}
          <p style={{ fontSize: '0.7rem', color: '#4a5568', margin: 0, padding: '6px 1.25rem 10px', textAlign: 'right' }}>
            {selectedRow.chartEngine === 'yahoo'
              ? `Yahoo Finance / yfinance · ${selectedRow.yahooTicker}`
              : `Powered by TradingView · ${selectedRow.tvSymbol}`}
          </p>
        </div>
        );
      })()}
    </div>
  );
}


// =============================================
// MarketRow — 테이블 행 (클릭 가능)
// =============================================
// market: 현재 시장 탭 ('crypto' | 'kospi' | 'kosdaq' | 'nasdaq' | 'dow')
// onRowClick: 클릭 시 handleRowClick 실행
function MarketRow({ row, rank, market, isSelected, onRowClick }) {
  const isCrypto = market === 'crypto';
  const isKRW    = market === 'kospi' || market === 'kosdaq';
  const isUS     = market === 'nasdaq' || market === 'dow';

  // 변동률 필드 (코인은 change_24h, 주식은 change_pct)
  const change = row.change_24h ?? row.change_pct ?? 0;
  const isUp   = change >= 0;
  const changeColor = isUp ? '#48bb78' : '#fc8181';

  // ── 가격 포맷 ──
  const formatPrice = () => {
    if (isKRW) {
      // 한국 주식: 원화 정수
      return `${(row.price ?? 0).toLocaleString()}원`;
    }
    if (isCrypto) {
      // 코인: USD, 가격대에 따라 소수점 자리 조정
      const p = row.price_usd ?? 0;
      if (p >= 10000) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
      if (p >= 1)     return `$${p.toFixed(2)}`;
      return `$${p.toFixed(4)}`;
    }
    if (isUS) {
      // 미국 주식: USD 소수점 2자리
      const p = row.price ?? 0;
      return `$${p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return '-';
  };

  const formatMarketCap = () => {
    if (!row.market_cap_usd) return '-';
    const b = row.market_cap_usd / 1e9;
    return b >= 1 ? `$${b.toFixed(1)}B` : `$${(row.market_cap_usd / 1e6).toFixed(0)}M`;
  };

  // 표시 이름 결정
  const primaryName = isCrypto ? row.symbol : row.name;
  const secondName  = isCrypto ? row.name : null; // 코인만 영문 이름 병기

  return (
    // ① 행 전체를 클릭 가능하게 — cursor: pointer로 클릭 표시
    <tr
      onClick={onRowClick}
      style={{
        borderBottom: '1px solid #2d3748',
        cursor: 'pointer',
        backgroundColor: isSelected ? '#1e2d3d' : 'transparent',
        transition: 'background-color 0.12s',
      }}
      title={`${primaryName} 차트 ${isSelected ? '닫기' : '보기'}`}
    >
      <Td>
        <span style={{
          display: 'inline-block',
          width: '24px', height: '24px',
          lineHeight: '24px', textAlign: 'center',
          borderRadius: '50%',
          backgroundColor: rank <= 3 ? '#3182ce' : '#2d3748',
          fontSize: '0.75rem', fontWeight: 'bold',
        }}>
          {rank}
        </span>
      </Td>
      <Td>
        <div>
          <span style={{ fontWeight: 'bold', color: isSelected ? '#63b3ed' : '#e2e8f0' }}>
            {primaryName}
          </span>
          {/* 코인: 영문 이름 병기 (예: "BTC Bitcoin") */}
          {secondName && (
            <span style={{ marginLeft: '6px', color: '#718096', fontSize: '0.8rem' }}>
              {secondName}
            </span>
          )}
          {/* 미국/한국 주식: 티커 코드 표시 (예: "AAPL") */}
          {isUS && (
            <span style={{ marginLeft: '6px', color: '#4a5568', fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {row.ticker}
            </span>
          )}
          {/* 선택된 행 표시 */}
          {isSelected && (
            <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: '#63b3ed' }}>
              ▼ 차트 보는 중
            </span>
          )}
        </div>
      </Td>
      <Td align="right">
        <span style={{ color: '#e2e8f0' }}>{formatPrice()}</span>
      </Td>
      <Td align="right">
        <span style={{
          color: changeColor, fontWeight: 'bold',
          padding: '2px 6px', borderRadius: '4px',
          backgroundColor: isUp ? '#1a3a2a' : '#3a1a1a',
          fontSize: '0.85rem',
        }}>
          {isUp ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
        </span>
      </Td>
      {isCrypto && (
        <Td align="right">
          <span style={{ color: '#a0aec0', fontSize: '0.85rem' }}>{formatMarketCap()}</span>
        </Td>
      )}
    </tr>
  );
}


// 테이블 헤더/셀
function Th({ children, align = 'left' }) {
  return (
    <th style={{
      padding: '10px 12px', textAlign: align,
      color: '#718096', fontSize: '0.8rem', fontWeight: '600',
      borderBottom: '2px solid #4a5568', whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  );
}

function Td({ children, align = 'left' }) {
  return (
    <td style={{ padding: '10px 12px', textAlign: align, verticalAlign: 'middle' }}>
      {children}
    </td>
  );
}


// 스타일 상수
const containerStyle = {
  backgroundColor: '#1a202c',
  borderRadius: '12px',
  padding: '1.25rem',
};

const tabGroupStyle = {
  display: 'flex', gap: '0.5rem',
  marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
};

const tabBtnStyle = (active, activeColor) => ({
  padding: '0.4rem 1rem', border: 'none', borderRadius: '6px',
  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 'bold',
  backgroundColor: active ? activeColor : '#2d3748',
  color: active ? 'white' : '#a0aec0',
  transition: 'all 0.15s',
});

const tableStyle = {
  width: '100%', borderCollapse: 'collapse',
  color: '#e2e8f0', fontSize: '0.9rem',
};

const loadingStyle = {
  padding: '2rem', textAlign: 'center', color: '#4a5568',
};
